import { useEffect, useRef } from 'react'

import ScrollView from '@/components/ui/ScrollView'
import { Icon } from '@/icons/Icon'

import { cn } from '@/lib/utils'
import type { Stack } from '@/payload-types'

type StackWithKeys = Stack

export type StackPickerProps = {
  stacks: StackWithKeys[]
  selectedStack: StackWithKeys | null
  setSelectedStack: (stack: StackWithKeys) => void
}

export default function StackPicker({ stacks, selectedStack, setSelectedStack }: StackPickerProps) {
  const itemRefs = useRef<Record<string | number, HTMLButtonElement | null>>({})

  useEffect(() => {
    if (!selectedStack) return

    const button = itemRefs.current[selectedStack.id]
    if (!button) return

    // Find the scroll container (the ScrollView's inner div)
    const scrollContainer = button.closest('.overflow-x-auto') as HTMLElement
    if (!scrollContainer) return

    const containerRect = scrollContainer.getBoundingClientRect()
    const buttonRect = button.getBoundingClientRect()

    // Calculate the current scroll position
    const currentScrollLeft = scrollContainer.scrollLeft

    // Calculate the button's position relative to the container
    const buttonLeft = buttonRect.left - containerRect.left + currentScrollLeft
    const buttonWidth = buttonRect.width
    const containerWidth = containerRect.width

    // Calculate the target scroll position to center the button
    const targetScrollLeft = buttonLeft - containerWidth / 2 + buttonWidth / 2

    // Apply smooth scroll with boundaries
    scrollContainer.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    })
  }, [selectedStack])

  return (
    <div className="lg:-translate-x-5">
      <ScrollView
        direction="horizontal"
        enhance={true}
        className="mx-auto mb-3 flex w-full max-w-[280px] items-center justify-start gap-6 px-5 pb-5 pt-2 sm:max-w-[340px] lg:mx-0 lg:max-w-[380px] lg:gap-8"
      >
        {stacks.map((stack) => (
          <button
            key={stack.id}
            ref={(el) => {
              itemRefs.current[stack.id] = el
            }}
            onClick={() => setSelectedStack(stack)}
            aria-label={stack.name ?? `Select stack ${stack.id}`}
            aria-pressed={selectedStack?.id === stack.id}
            className={cn(
              'flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-white/15 bg-black text-white opacity-50 transition-all hover:opacity-100',
              selectedStack?.id === stack.id &&
                'border-primary-500 opacity-100 shadow-[0px_5px_15px_5px_rgba(255,52,101,0.30)]',
            )}
          >
            {stack.icon && <Icon name={stack.icon} />}
          </button>
        ))}
      </ScrollView>
    </div>
  )
}
